import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigationType } from 'react-router';
import { exerciseLink } from '@erectus/shared';
import type { SessionSummary } from '@erectus/shared';
import { Card, SectionTitle } from '@/ui/primitives.js';
import { Icon } from '@/ui/icons.js';
import { SCORE_BANDS, scoreColor } from '@/ui/score.js';
import { EXERCISE_LIBRARY, localDayKey, type ExerciseLogEntry } from '@/lib/repo.js';

type Session = SessionSummary & { id: string };
type View = 'week' | 'month';

const VIEW_KEY = 'erectus.activityView';
const DAY_MS = 24 * 60 * 60 * 1000;

/** The day picked last, so coming back from a report shows the same day (this app run only). */
let lastSelected: string | null = null;

function storedView(): View {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'month' ? 'month' : 'week';
  } catch {
    return 'week';
  }
}


interface DayActivity {
  sessions: Session[];
  exercises: ExerciseLogEntry[];
}

/**
 * Activity at the top of Progress: sessions and exercises on one calendar.
 *
 * Week view by default — seven days fit in a strip and keep Progress short;
 * Month expands it. A day shows a dot for sessions (coloured by its best
 * score) and a ring for exercises done. Tapping a day lists both: sessions open
 * their report, exercises open the exercise. The full session list stays at
 * the bottom of Progress as the other way into reports.
 */
export function ActivityCalendar({
  sessions,
  exerciseLog,
}: {
  sessions: readonly Session[];
  exerciseLog: readonly ExerciseLogEntry[];
}): ReactNode {
  const [view, setView] = useState<View>(storedView);
  const today = localDayKey(Date.now());
  const back = useNavigationType() === 'POP';
  const [selected, setSelectedState] = useState<string>(() => (back && lastSelected) || today);
  const setSelected = (day: string): void => {
    lastSelected = day;
    setSelectedState(day);
  };

  const byDay = useMemo(() => {
    const map = new Map<string, DayActivity>();
    const at = (key: string): DayActivity => {
      let day = map.get(key);
      if (!day) map.set(key, (day = { sessions: [], exercises: [] }));
      return day;
    };
    for (const s of sessions) at(localDayKey(s.startedAt)).sessions.push(s);
    for (const e of exerciseLog) at(localDayKey(e.doneAt)).exercises.push(e);
    return map;
  }, [sessions, exerciseLog]);

  const choose = (next: View): void => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Remembering the view is a convenience.
    }
  };

  const day = byDay.get(selected);

  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between">
        <SectionTitle>Activity</SectionTitle>
        <div className="flex rounded-full bg-surface p-0.5 text-xs font-medium" role="tablist">
          {(['week', 'month'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={view === option}
              onClick={() => choose(option)}
              className={`rounded-full px-3 py-1 ${view === option ? 'bg-ink text-white' : 'text-ink-soft'}`}
            >
              {option === 'week' ? 'Week' : 'Month'}
            </button>
          ))}
        </div>
      </div>

      {view === 'week' ? (
        <WeekStrip byDay={byDay} selected={selected} onSelect={setSelected} today={today} />
      ) : (
        <MonthGrid byDay={byDay} selected={selected} onSelect={setSelected} today={today} />
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
        <span className="font-medium text-ink-soft">Session score</span>
        {SCORE_BANDS.map((band) => (
          <span key={band.label} className="flex items-center gap-1">
            <span className="size-2 rounded-full" style={{ backgroundColor: band.color }} />
            {band.label}
          </span>
        ))}
        <span className="ml-auto flex items-center gap-1">
          <span className="size-2 rounded-full border-2 border-ink" /> Exercise
        </span>
      </div>

      <DayDetail dayKey={selected} activity={day} today={today} />
    </Card>
  );
}

function DayCell({
  dayKey,
  label,
  activity,
  selected,
  isToday,
  onSelect,
  disabled = false,
}: {
  dayKey: string;
  label: string;
  activity: DayActivity | undefined;
  selected: boolean;
  isToday: boolean;
  onSelect(key: string): void;
  disabled?: boolean;
}): ReactNode {
  const best = activity?.sessions.length ? Math.max(...activity.sessions.map((s) => s.score)) : null;
  const exercised = (activity?.exercises.length ?? 0) > 0;
  const any = best !== null || exercised;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(dayKey)}
      aria-pressed={selected}
      aria-label={`${dayKey}${best !== null ? `, ${activity!.sessions.length} session(s)` : ''}${exercised ? `, ${activity!.exercises.length} exercise(s)` : ''}`}
      className={`mx-auto flex size-10 flex-col items-center justify-center gap-0.5 rounded-full text-sm ${
        selected ? 'bg-ink text-white' : any ? 'text-ink' : 'text-muted/60'
      } ${isToday && !selected ? 'ring-1 ring-line' : ''} disabled:opacity-30`}
    >
      <span className="leading-none">{label}</span>
      <span className="flex h-1.5 items-center gap-0.5">
        {best !== null ? (
          <span className="size-1.5 rounded-full" style={{ backgroundColor: selected ? '#fff' : scoreColor(best) }} />
        ) : null}
        {exercised ? (
          <span className={`size-1.5 rounded-full border ${selected ? 'border-white' : 'border-ink'}`} />
        ) : null}
      </span>
    </button>
  );
}

function WeekStrip({
  byDay,
  selected,
  onSelect,
  today,
}: {
  byDay: Map<string, DayActivity>;
  selected: string;
  onSelect(key: string): void;
  today: string;
}): ReactNode {
  const [offset, setOffset] = useState(0);
  const now = new Date();
  const sunday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() + offset * 7);
  const days = Array.from({ length: 7 }, (_, i) => new Date(sunday.getTime() + i * DAY_MS));
  const range = `${days[0]!.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${days[6]!.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => setOffset((o) => o - 1)} aria-label="Previous week" className="grid size-8 place-items-center text-ink-soft">
          <Icon name="back" size={18} />
        </button>
        <span className="text-xs font-semibold text-ink-soft">{offset === 0 ? 'This week' : range}</span>
        <button
          type="button"
          onClick={() => setOffset((o) => o + 1)}
          disabled={offset >= 0}
          aria-label="Next week"
          className="grid size-8 place-items-center text-ink-soft disabled:opacity-30"
        >
          <Icon name="back" size={18} className="rotate-180" />
        </button>
      </div>
      <div className="grid grid-cols-7 text-center">
        {days.map((d) => {
          const key = localDayKey(d.getTime());
          return (
            <div key={key} className="space-y-1">
              <span className="block text-[10px] font-semibold text-muted">
                {d.toLocaleDateString(undefined, { weekday: 'narrow' })}
              </span>
              <DayCell
                dayKey={key}
                label={String(d.getDate())}
                activity={byDay.get(key)}
                selected={selected === key}
                isToday={key === today}
                onSelect={onSelect}
                disabled={key > today}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthGrid({
  byDay,
  selected,
  onSelect,
  today,
}: {
  byDay: Map<string, DayActivity>;
  selected: string;
  onSelect(key: string): void;
  today: string;
}): ReactNode {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const thisMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => setMonth(new Date(year, monthIndex - 1, 1))} aria-label="Previous month" className="grid size-8 place-items-center text-ink-soft">
          <Icon name="back" size={18} />
        </button>
        <span className="text-sm font-semibold text-ink">
          {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
        </span>
        <button
          type="button"
          onClick={() => setMonth(new Date(year, monthIndex + 1, 1))}
          disabled={month.getTime() >= thisMonth}
          aria-label="Next month"
          className="grid size-8 place-items-center text-ink-soft disabled:opacity-30"
        >
          <Icon name="back" size={18} className="rotate-180" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-y-1 text-center">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <span key={i} className="text-[10px] font-semibold text-muted">
            {d}
          </span>
        ))}
        {Array.from({ length: month.getDay() }, (_, i) => (
          <span key={`pad-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const key = localDayKey(new Date(year, monthIndex, i + 1).getTime());
          return (
            <DayCell
              key={key}
              dayKey={key}
              label={String(i + 1)}
              activity={byDay.get(key)}
              selected={selected === key}
              isToday={key === today}
              onSelect={onSelect}
              disabled={key > today}
            />
          );
        })}
      </div>
    </div>
  );
}

function DayDetail({
  dayKey,
  activity,
  today,
}: {
  dayKey: string;
  activity: DayActivity | undefined;
  today: string;
}): ReactNode {
  const [y, m, d] = dayKey.split('-').map(Number);
  const title =
    dayKey === today
      ? 'Today'
      : new Date(y!, m! - 1, d!).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });

  if (!activity || (activity.sessions.length === 0 && activity.exercises.length === 0)) {
    return (
      <p className="border-t border-line pt-3 text-xs text-muted">
        {title}: nothing recorded{dayKey === today ? ' yet' : ''}.
      </p>
    );
  }

  return (
    <div className="space-y-2 border-t border-line pt-3">
      <p className="text-xs font-semibold text-ink-soft">{title}</p>
      <ul className="space-y-1">
        {[...activity.sessions]
          .sort((a, b) => a.startedAt - b.startedAt)
          .map((session) => (
            <li key={session.id}>
              <Link to={`/report/${session.id}`} state={{ from: 'progress' }} className="flex items-center justify-between gap-3 rounded-lg bg-surface px-3 py-2 text-sm">
                <span className="flex items-center gap-2">
                  <Icon name="session" size={16} className="text-muted" />
                  Session ·{' '}
                  {new Date(session.startedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} ·{' '}
                  {Math.round(session.durationSec / 60)} min
                </span>
                <span className="flex items-center gap-1 font-bold" style={{ color: scoreColor(session.score) }}>
                  {session.score}
                  <Icon name="back" size={14} className="rotate-180 text-muted" />
                </span>
              </Link>
            </li>
          ))}
        {[...activity.exercises]
          .sort((a, b) => a.doneAt - b.doneAt)
          .map((entry) => {
            const exercise = EXERCISE_LIBRARY.find((e) => e.id === entry.exerciseId);
            return (
              <li key={`${entry.exerciseId}-${entry.doneAt}`}>
                <Link to={exerciseLink(entry.exerciseId)} state={{ from: 'progress' }} className="flex items-center justify-between gap-3 rounded-lg bg-surface px-3 py-2 text-sm">
                  <span className="flex items-center gap-2">
                    <Icon name="exercises" size={16} className="text-muted" />
                    {exercise?.name ?? entry.exerciseId}
                  </span>
                  <Icon name="check" size={16} className="text-[color:var(--color-state-good)]" />
                </Link>
              </li>
            );
          })}
      </ul>
    </div>
  );
}
